import { AppStateEnum } from '../../../../../main/types/app-state.types.ts';
import { getSpeechRecognitionUtil } from '../../../../../main/utils/audio/speech-recognition.util.ts';

import { SPEECH_RECOGNITION_MESSAGES } from '../../../../constants/speech-recognition.ts';
import { selectCurrentLessonTaskStep } from '../../../../selectors/current-lesson-task-step.selector.ts';
import { updateAppStateThunk } from '../../../../slices/app-state/thunks/update-app-state.thunk.ts';
import { setBallIsVisible } from '../../../../slices/ball/ball.slice.ts';

import { BaseAppStateChangeHandlerUtil } from '../base-app-state-change.handler.ts';

/**
 * Handler for app question steps (AppAsk)
 * Used in various tasks including NewCritter and PictureComprehension
 * Handles speech recognition, response validation, and state transitions
 */
export class AppAskStateChangeHandlerUtil extends BaseAppStateChangeHandlerUtil {
  private static _instance: AppAskStateChangeHandlerUtil;
  private lastRecognizedText: string | null = null;

  public static get instance(): AppAskStateChangeHandlerUtil {
    if (!AppAskStateChangeHandlerUtil._instance) {
      AppAskStateChangeHandlerUtil._instance = new AppAskStateChangeHandlerUtil();
    }
    return AppAskStateChangeHandlerUtil._instance;
  }

  private constructor(private readonly speechRecognitionUtil = getSpeechRecognitionUtil()) {
    super();
  }

  protected getHandlerName(): string {
    return 'AppAsk';
  }

  protected override async onReadyToStart(): Promise<void> {
    await super.onReadyToStart();
    console.log('AppAsk onReadyToStart');

    await this.handleStartStep();
  }

  protected override async onRecording(): Promise<void> {
    console.log('AppAsk onRecording');
    const dispatch = await this.reduxStoreUtil.getDispatch();
    const state = await this.reduxStoreUtil.getRootState();
    // Show ball during recording
    dispatch(setBallIsVisible(true));

    try {
      // Start speech recognition with silence detection
      const result = await this.speechRecognitionUtil.recognizeSpeech({
        silenceDetection: {
          enabled: true,
        },
      });

      // Get current task and step
      const { task, step } = selectCurrentLessonTaskStep(state);

      if (result.success && result.transcript) {
        this.lastRecognizedText = result.transcript;
        await this.handleAppAskStepEvaluation(task, step, result.transcript);
      } else {
        await this.handleRetryAfterError();
      }
    } catch (error) {
      this.handleError(error as Error);
      await this.handleRetryAfterError();
    }
  }

  protected override async onCorrect(): Promise<void> {
    console.log('AppAsk onCorrect');
    await this.handleCorrect({
      assuming: false,
      answer: this.lastRecognizedText ?? SPEECH_RECOGNITION_MESSAGES.NO_RESPONSE,
    });
  }

  protected override async onAssumeCorrect(): Promise<void> {
    console.log('AppAsk onAssumeCorrect');
    await this.handleCorrect({
      assuming: true,
      answer: this.lastRecognizedText ?? SPEECH_RECOGNITION_MESSAGES.NO_RESPONSE,
    });
  }

  protected override async onIncorrect(): Promise<void> {
    console.log('AppAsk onIncorrect');
    await this.handleIncorrect(this.lastRecognizedText ?? SPEECH_RECOGNITION_MESSAGES.NO_RESPONSE);
  }

  protected override async onRetry(): Promise<void> {
    await super.onRetry();
    console.log('AppAsk onRetry');

    await this.handleStartStep();
  }

  protected override async onReadyForNextStep(): Promise<void> {
    await super.onReadyForNextStep();
    console.log('AppAsk onReadyForNextStep');
    await this.handleReadyForNextStep();
  }

  /**
   * Handle Aborting state - interrupts speech and recognition
   */
  protected override async onAborting(): Promise<void> {
    await super.onAborting();
    console.log('AppAsk onAborting');
    await this.speechRecognitionUtil.stopRecognizingSpeech();
  }

  private async handleStartStep(): Promise<void> {
    this.lastRecognizedText = null;
    const dispatch = await this.reduxStoreUtil.getDispatch();

    // Hide ball initially
    await this.resetBallPosition();

    // Speak the question
    await this.audioPlayerUtil.speakStep();
    await this.audioPlayerUtil.waitSpeakCompletion();

    // Transition to recording state to capture user's answer
    await dispatch(updateAppStateThunk({ state: AppStateEnum.Recording, blockAnimation: true })).unwrap();
  }
}

export const getAppAskStateChangeHandlerUtil = (): AppAskStateChangeHandlerUtil =>
  AppAskStateChangeHandlerUtil.instance;
